import { useState } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { useForm } from 'react-hook-form'
import { Eye, EyeOff, Mail, Lock, User, Building2, Zap } from 'lucide-react'
import { useSpring, animated } from '@react-spring/web'
import { toast } from 'sonner'
import { Input } from '@/components/common/Input'
import { Button } from '@/components/common/Button'
import { useAuthStore } from '@/stores/authStore'
import { useBrandingStore } from '@/stores/brandingStore'
import AuthService from '@/services/auth.service'

interface RegisterFormData {
  full_name: string
  email: string
  workspace_name: string
  password: string
  confirm_password: string
}

export default function RegisterPage() {
  const [showPassword, setShowPassword] = useState(false)
  const [isLoading, setIsLoading] = useState(false)
  const navigate = useNavigate()
  const setTokens = useAuthStore((s) => s.setTokens)
  const platformName = useBrandingStore((state) => state.platform_name)

  const {
    register,
    handleSubmit,
    watch,
    formState: { errors },
  } = useForm<RegisterFormData>()

  const password = watch('password')

  const cardSpring = useSpring({
    from: { opacity: 0, transform: 'translateY(24px)' },
    to: { opacity: 1, transform: 'translateY(0px)' },
    config: { tension: 280, friction: 26 },
  })

  async function onSubmit(data: RegisterFormData) {
    setIsLoading(true)
    try {
      const tokens = await AuthService.register({
        full_name: data.full_name,
        email: data.email,
        password: data.password,
        workspace_name: data.workspace_name,
      })
      setTokens(tokens)
      toast.success('Conta criada com sucesso!')
      navigate('/dashboard', { replace: true })
    } catch (error: unknown) {
      const err = error as { response?: { data?: { detail?: string } } }
      toast.error(err.response?.data?.detail ?? 'Erro ao criar conta. Tente novamente.')
    } finally {
      setIsLoading(false)
    }
  }

  return (
    <div className="auth-bg min-h-screen flex items-center justify-center p-4">
      <animated.div style={cardSpring} className="w-full max-w-md">
        {/* Logo */}
        <div className="flex flex-col items-center mb-8">
          <div className="w-14 h-14 rounded-2xl bg-gradient-to-br from-[var(--color-brand)] to-[var(--color-secondary)] flex items-center justify-center mb-4 shadow-[var(--shadow-glow)]">
            <Zap size={28} className="text-white" />
          </div>
          <h1 className="text-2xl font-bold text-[var(--text-primary)]">
            Criar conta no <span className="gradient-text">{platformName || 'ISing'}</span>
          </h1>
          <p className="text-sm text-[var(--text-muted)] mt-1">
            Comece seu atendimento omnichannel
          </p>
        </div>

        {/* Card */}
        <div className="glass-strong p-8">
          <form onSubmit={handleSubmit(onSubmit)} className="flex flex-col gap-4">
            <Input
              label="Nome completo"
              placeholder="João Silva"
              leftIcon={<User size={16} />}
              error={errors.full_name?.message}
              {...register('full_name', {
                required: 'Nome é obrigatório',
                minLength: {
                  value: 2,
                  message: 'Nome deve ter pelo menos 2 caracteres',
                },
              })}
            />

            <Input
              label="E-mail"
              type="email"
              autoComplete="email"
              placeholder="seu@email.com"
              leftIcon={<Mail size={16} />}
              error={errors.email?.message}
              {...register('email', {
                required: 'E-mail é obrigatório',
                pattern: {
                  value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
                  message: 'E-mail inválido',
                },
              })}
            />

            <Input
              label="Nome do workspace"
              placeholder="Minha Empresa"
              leftIcon={<Building2 size={16} />}
              error={errors.workspace_name?.message}
              {...register('workspace_name', {
                required: 'Nome do workspace é obrigatório',
                minLength: {
                  value: 2,
                  message: 'Nome deve ter pelo menos 2 caracteres',
                },
              })}
            />

            <Input
              label="Senha"
              type={showPassword ? 'text' : 'password'}
              autoComplete="new-password"
              placeholder="Mínimo 8 caracteres"
              leftIcon={<Lock size={16} />}
              rightIcon={showPassword ? <EyeOff size={16} /> : <Eye size={16} />}
              onRightIconClick={() => setShowPassword((v) => !v)}
              error={errors.password?.message}
              {...register('password', {
                required: 'Senha é obrigatória',
                minLength: {
                  value: 8,
                  message: 'Senha deve ter pelo menos 8 caracteres',
                },
              })}
            />

            <Input
              label="Confirmar senha"
              type={showPassword ? 'text' : 'password'}
              autoComplete="new-password"
              placeholder="Repita sua senha"
              leftIcon={<Lock size={16} />}
              error={errors.confirm_password?.message}
              {...register('confirm_password', {
                required: 'Confirmação de senha é obrigatória',
                validate: (value) =>
                  value === password || 'As senhas não coincidem',
              })}
            />

            <Button
              type="submit"
              fullWidth
              loading={isLoading}
              size="lg"
              className="mt-2"
            >
              Criar conta
            </Button>
          </form>

          <p className="text-center text-sm text-[var(--text-muted)] mt-6">
            Já tem uma conta?{' '}
            <Link
              to="/login"
              className="text-[var(--color-brand)] font-medium hover:text-[var(--color-brand-hover)] transition-colors duration-150"
            >
              Fazer login
            </Link>
          </p>
        </div>
      </animated.div>
    </div>
  )
}
